import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, Gamepad2, RotateCcw, Save, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { gameplayApi, type GameplayTrajectoryPoint, type GameplayVector } from '@/api/gameplayApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { useUiStore } from '@/stores/uiStore'
import { Slider } from '@/components/ui/slider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Chapter, Project } from '@/types/nf'

export type GameplayWeightKey =
  | 'core_loop_weight'
  | 'agency_weight'
  | 'info_weight'
  | 'social_weight'
  | 'reward_weight'

export interface GameplayDimension {
  id: 'coreLoop' | 'agency' | 'info' | 'social' | 'reward'
  categoryKey: keyof GameplayVector
  weightKey: GameplayWeightKey
}

export const GAMEPLAY_DIMENSIONS: GameplayDimension[] = [
  { id: 'coreLoop', categoryKey: 'core_loop', weightKey: 'core_loop_weight' },
  { id: 'agency', categoryKey: 'agency', weightKey: 'agency_weight' },
  { id: 'info', categoryKey: 'info_asymmetry', weightKey: 'info_weight' },
  { id: 'social', categoryKey: 'social_frame', weightKey: 'social_weight' },
  { id: 'reward', categoryKey: 'reward_schedule', weightKey: 'reward_weight' },
]

const TRAJECTORY_SERIES = [
  { key: 'target_core', className: 'stroke-primary', dash: '' },
  { key: 'observed_core', className: 'stroke-emerald-500', dash: '4 4' },
  { key: 'target_payoff', className: 'stroke-amber-500', dash: '' },
  { key: 'observed_payoff', className: 'stroke-sky-500', dash: '4 4' },
  { key: 'score', className: 'stroke-foreground', dash: '2 5' },
] as const

type TrajectoryMetric = (typeof TRAJECTORY_SERIES)[number]['key']

function clamp01(value: number | undefined) {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value ?? 0))
}

function formatWeight(value: number | undefined) {
  return `${Math.round(clamp01(value) * 100)}%`
}

export function buildGameplayVectorPatch(
  vector: GameplayVector | undefined,
  key: GameplayWeightKey,
  value: number,
): GameplayVector {
  return { ...(vector ?? {}), [key]: clamp01(value) }
}

export function summarizeGameplayDimensions(vector: GameplayVector | undefined) {
  return [...GAMEPLAY_DIMENSIONS]
    .map((dimension) => ({ ...dimension, value: clamp01(vector?.[dimension.weightKey]) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
}

function coordinate(value: number, size: number) {
  return Number(((1 - clamp01(value)) * size).toFixed(2))
}

export function buildGameplayPath(
  points: GameplayTrajectoryPoint[],
  metric: TrajectoryMetric,
  width: number,
  height: number,
) {
  if (!points.length) return ''
  const maxIndex = Math.max(1, points.length - 1)
  return points
    .map((point, index) => {
      const x = Number(((index / maxIndex) * width).toFixed(2))
      const y = coordinate(point[metric], height)
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`
    })
    .join(' ')
}

function mergeVector(base: GameplayVector | undefined, fallback: GameplayVector | undefined) {
  return { ...(fallback ?? {}), ...(base ?? {}) }
}

function GameplayValueText({ value, className }: { value: string; className?: string }) {
  const { t } = useTranslation()
  const label = t(`gameplay.values.${value}`, { defaultValue: value })
  const hint = t(`gameplay.valueHints.${value}`, { defaultValue: '' })
  if (!hint) return <span className={className}>{label}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('cursor-help', className)}>{label}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72">
        {hint}
      </TooltipContent>
    </Tooltip>
  )
}

function DimensionControl({
  dimension,
  vector,
  advanced,
  disabled,
  onChange,
}: {
  dimension: GameplayDimension
  vector: GameplayVector | undefined
  advanced: boolean
  disabled: boolean
  onChange: (key: GameplayWeightKey, value: number) => void
}) {
  const { t } = useTranslation()
  const weight = clamp01(vector?.[dimension.weightKey])
  const category = vector?.[dimension.categoryKey]
  const label = t(`gameplay.dimensions.${dimension.id}.label`)
  return (
    <div className="space-y-2 rounded-md border bg-background/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="w-fit cursor-help text-sm font-medium" tabIndex={0}>
                {label}
              </p>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-72">
              {t(`gameplay.dimensions.${dimension.id}.hint`)}
            </TooltipContent>
          </Tooltip>
          <p className="break-words text-xs text-muted-foreground">
            {category ? <GameplayValueText value={String(category)} /> : t('gameplay.unset')}
          </p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="cursor-help rounded-md" tabIndex={0}>
              {formatWeight(weight)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-72">
            {t('gameplay.weightHint')}
          </TooltipContent>
        </Tooltip>
      </div>
      {advanced ? (
        <Slider
          value={[weight]}
          min={0}
          max={1}
          step={0.01}
          disabled={disabled}
          onValueChange={(value) => onChange(dimension.weightKey, value[0] ?? 0)}
          aria-label={label}
        />
      ) : (
        <Progress value={weight * 100} />
      )}
    </div>
  )
}

function VectorControls({
  vector,
  advanced,
  disabled,
  onChange,
}: {
  vector: GameplayVector | undefined
  advanced: boolean
  disabled: boolean
  onChange: (key: GameplayWeightKey, value: number) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      {GAMEPLAY_DIMENSIONS.map((dimension) => (
        <DimensionControl
          key={dimension.weightKey}
          dimension={dimension}
          vector={vector}
          advanced={advanced}
          disabled={disabled}
          onChange={onChange}
        />
      ))}
    </div>
  )
}

function TrajectoryChart({ points }: { points: GameplayTrajectoryPoint[] }) {
  const { t } = useTranslation()
  const width = 640
  const height = 220
  const sortedPoints = useMemo(() => [...points].sort((a, b) => a.chapter_no - b.chapter_no), [points])
  const maxIndex = Math.max(1, sortedPoints.length - 1)
  const pointX = (index: number) => Number(((index / maxIndex) * width).toFixed(2))
  const pointY = (value: number) => coordinate(value, height)

  if (!sortedPoints.length) {
    return (
      <div className="grid min-h-48 place-items-center rounded-md border bg-muted/30 text-sm text-muted-foreground">
        {t('gameplay.trajectory.empty')}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <svg viewBox={`0 0 ${width} ${height + 36}`} role="img" aria-label={t('gameplay.trajectory.title')} className="h-72 w-full">
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
          <g key={tick}>
            <line x1={0} x2={width} y1={pointY(tick)} y2={pointY(tick)} className="stroke-border" strokeWidth="1" />
            <text x={4} y={pointY(tick) - 4} className="fill-muted-foreground text-[10px]">
              {Math.round(tick * 100)}
            </text>
          </g>
        ))}
        {TRAJECTORY_SERIES.map((series) => (
          <path
            key={series.key}
            d={buildGameplayPath(sortedPoints, series.key, width, height)}
            fill="none"
            className={cn(series.className, 'transition-all')}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={series.dash}
          />
        ))}
        {sortedPoints.map((point, index) => (
          <g key={point.chapter_no}>
            <line x1={pointX(index)} x2={pointX(index)} y1={height} y2={height + 6} className="stroke-muted-foreground" />
            <text x={pointX(index)} y={height + 22} textAnchor="middle" className="fill-muted-foreground text-[10px]">
              {point.chapter_no}
            </text>
          </g>
        ))}
      </svg>
      <div className="flex flex-wrap gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
        {TRAJECTORY_SERIES.map((series) => (
          <span key={series.key} className="inline-flex items-center gap-1.5">
            <span className={cn('h-0.5 w-5 rounded-full', series.className.replace('stroke-', 'bg-'))} />
            {t(`gameplay.trajectory.series.${series.key}`)}
          </span>
        ))}
      </div>
    </div>
  )
}

export function GameplayPanel({
  project,
  chapter,
  branchId,
  chapters = [],
  onSelectChapter,
}: {
  project: Project
  chapter: Chapter | null
  branchId?: string
  chapters?: Chapter[]
  onSelectChapter?: (id: string) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // 档位改由全局 contentMode 决定, 不再有本面板私有的「高级模式」开关 ——
  // 一个页面两个高级开关会互相打架。玩法页签在简易档位下整体隐藏,
  // 因此这里进来即视为高级。
  const advanced = useUiStore((s) => s.contentMode) === 'advanced'
  const [profileVector, setProfileVector] = useState<GameplayVector | undefined>()
  const [draftTemplateKey, setDraftTemplateKey] = useState<string>()
  const [chapterVector, setChapterVector] = useState<GameplayVector | undefined>()
  const profileQueryKey = ['gameplay-profile', project.id] as const
  const chapterQueryKey = ['chapter-gameplay', project.id, branchId ?? 'main', chapter?.chapter_number] as const
  const trajectoryQueryKey = ['gameplay-trajectory', project.id, branchId ?? 'main'] as const

  const templatesQuery = useQuery({
    queryKey: ['gameplay-templates'],
    queryFn: gameplayApi.listTemplates,
  })

  const profileQuery = useQuery({
    queryKey: profileQueryKey,
    queryFn: () => gameplayApi.getProfile(project.id),
    retry: false,
  })

  const chapterQuery = useQuery({
    queryKey: chapterQueryKey,
    queryFn: () => gameplayApi.getChapterGameplay(project.id, chapter!.chapter_number, branchId),
    enabled: Boolean(chapter),
    retry: false,
  })

  const trajectoryQuery = useQuery({
    queryKey: trajectoryQueryKey,
    queryFn: () => gameplayApi.getTrajectory(project.id, branchId),
    retry: false,
  })

  useEffect(() => {
    setProfileVector(profileQuery.data?.target_vector)
  }, [profileQuery.data?.target_vector, project.id])

  useEffect(() => {
    setChapterVector(mergeVector(chapterQuery.data?.target_vector, profileQuery.data?.target_vector))
  }, [chapter?.chapter_number, chapterQuery.data?.target_vector, profileQuery.data?.target_vector])

  const selectedTemplateKey = draftTemplateKey ?? (profileQuery.data?.template_key || templatesQuery.data?.find((tpl) => tpl.template_key === project.genre)?.template_key)
  const selectedTemplate = templatesQuery.data?.find((tpl) => tpl.template_key === selectedTemplateKey)
  const profileTop = summarizeGameplayDimensions(profileVector)

  const profileMutation = useMutation({
    mutationFn: ({ vector, templateKey }: { vector: GameplayVector; templateKey?: string }) =>
      gameplayApi.putProfile(project.id, {
        version: profileQuery.data?.version,
        template_key: templateKey ?? profileQuery.data?.template_key ?? selectedTemplate?.template_key,
        target_vector: vector,
        cadence_json: profileQuery.data?.cadence_json,
        forbidden_mix_json: profileQuery.data?.forbidden_mix_json,
      }),
    onSuccess: async (data) => {
      setDraftTemplateKey(undefined)
      setProfileVector(data.target_vector)
      queryClient.setQueryData(profileQueryKey, data)
      await queryClient.invalidateQueries({ queryKey: trajectoryQueryKey })
      toast.success(t('gameplay.profile.saved'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('gameplay.profile.saveFailed'))),
  })

  const chapterMutation = useMutation({
    mutationFn: (vector: GameplayVector) => gameplayApi.putChapterTarget(project.id, chapter!.chapter_number, { target_vector: vector }, branchId),
    onSuccess: async (data) => {
      setChapterVector(data.target_vector)
      queryClient.setQueryData(chapterQueryKey, data)
      await queryClient.invalidateQueries({ queryKey: trajectoryQueryKey })
      toast.success(t('gameplay.chapter.saved'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('gameplay.chapter.saveFailed'))),
  })

  const selectTemplate = (templateKey: string) => {
    const template = templatesQuery.data?.find((item) => item.template_key === templateKey)
    if (!template) return
    setProfileVector(template.default_vector)
    setDraftTemplateKey(templateKey)
  }

  const resetProfileFromTemplate = () => {
    if (!selectedTemplate) return
    setProfileVector(selectedTemplate.default_vector)
  }

  const saveProfile = () => {
    if (!profileVector?.core_loop) return
    profileMutation.mutate({ vector: profileVector, templateKey: selectedTemplateKey })
  }

  const saveChapter = () => {
    if (!chapter || !chapterVector?.core_loop) return
    chapterMutation.mutate(chapterVector)
  }

  const profileBusy = profileMutation.isPending
  const chapterBusy = chapterMutation.isPending

  if (templatesQuery.isLoading || profileQuery.isLoading) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-lg">
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Gamepad2 className="size-4 text-primary" />
                {t('gameplay.profile.title')}
              </CardTitle>
              <CardDescription>{t('gameplay.profile.description')}</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={selectedTemplateKey} onValueChange={selectTemplate} disabled={profileBusy}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('gameplay.profile.templatePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(templatesQuery.data ?? []).map((template) => (
                    <SelectItem key={template.template_key} value={template.template_key} textValue={template.name}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{template.name}</span>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-80">
                          {template.core_loop_desc || template.reader_appetite}
                        </TooltipContent>
                      </Tooltip>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={resetProfileFromTemplate} disabled={!selectedTemplate || profileBusy}>
                <RotateCcw className="size-4" />
                {t('gameplay.profile.reset')}
              </Button>
              <Button type="button" size="sm" onClick={saveProfile} disabled={!profileVector?.core_loop || profileBusy}>
                <Save className="size-4" />
                {profileBusy ? t('gameplay.saving') : t('common.save')}
              </Button>
            </div>
          </div>
          {selectedTemplate ? (
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-sm md:grid-cols-2">
              <p>
                <span className="text-muted-foreground">{t('gameplay.profile.coreLoopDesc')}: </span>
                {selectedTemplate.core_loop_desc}
              </p>
              <p>
                <span className="text-muted-foreground">{t('gameplay.profile.readerAppetite')}: </span>
                {selectedTemplate.reader_appetite}
              </p>
            </div>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {!profileVector?.core_loop ? (
            <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              {t('gameplay.profile.empty')}
            </p>
          ) : null}
          <VectorControls
            vector={profileVector}
            advanced={advanced}
            disabled={profileBusy}
            onChange={(key, value) => setProfileVector((current) => buildGameplayVectorPatch(current, key, value))}
          />
          {!advanced && profileTop.length ? (
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {profileTop.map((dimension) => (
                <Badge key={dimension.weightKey} variant="secondary" className="rounded-md">
                  {t(`gameplay.dimensions.${dimension.id}.label`)} · {formatWeight(dimension.value)}
                  {profileVector?.[dimension.categoryKey] ? (
                    <>
                      {' · '}
                      <GameplayValueText value={String(profileVector[dimension.categoryKey])} />
                    </>
                  ) : null}
                </Badge>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="size-4 text-primary" />
              {t('gameplay.chapter.title')}
            </CardTitle>
            {onSelectChapter ? (
              <Select value={chapter?.id ?? ''} onValueChange={onSelectChapter} disabled={chapterBusy}>
                <SelectTrigger id="gameplay-chapter-select" aria-label={t('gameplay.chapter.selectChapter')}><SelectValue placeholder={t('gameplay.chapter.selectChapter')} /></SelectTrigger>
                <SelectContent>{chapters.map((item) => <SelectItem key={item.id} value={item.id}>{item.chapter_number} · {item.title || t('chapter.untitled')}</SelectItem>)}</SelectContent>
              </Select>
            ) : null}
            <CardDescription>
              {chapter
                ? t('gameplay.chapter.subtitle', { chapter: chapter.chapter_number, title: chapter.title || t('chapter.untitled') })
                : t('gameplay.chapter.selectChapter')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {chapterQuery.isLoading ? <Skeleton className="h-28 rounded-lg" /> : null}
            <VectorControls
              vector={chapterVector}
              advanced={advanced}
              disabled={!chapter || chapterBusy}
              onChange={(key, value) => setChapterVector((current) => buildGameplayVectorPatch(current, key, value))}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {chapterQuery.data?.status ? t('gameplay.chapter.status', { status: chapterQuery.data.status }) : t('gameplay.chapter.pendingState')}
              </p>
              <Button type="button" size="sm" onClick={saveChapter} disabled={!chapter || !chapterVector?.core_loop || chapterBusy}>
                <Save className="size-4" />
                {chapterBusy ? t('gameplay.saving') : t('gameplay.chapter.saveTarget')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="size-4 text-primary" />
              {t('gameplay.trajectory.title')}
            </CardTitle>
            <CardDescription>{t('gameplay.trajectory.description')}</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            {trajectoryQuery.isLoading ? (
              <Skeleton className="h-72 rounded-lg" />
            ) : trajectoryQuery.isError ? null : (
              <TrajectoryChart points={trajectoryQuery.data?.trajectory ?? []} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
